import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Trophy } from "lucide-react";
import Seo from "@/components/Seo";
import ProductCard from "@/components/ProductCard";
import { api } from "@/lib/api";

const yearOf = (id) => {
  const m = id.match(/-(\d{4})$/);
  return m ? parseInt(m[1], 10) : null;
};
const baseOf = (id) => id.replace(/^champion-/, "").replace(/-\d{4}$/, "");

export default function ChampionsPage() {
  const [products, setProducts] = useState(null);
  const [decade, setDecade] = useState(null);

  useEffect(() => {
    api.get("/products?category=champions")
      .then((r) => setProducts(r.data || []))
      .catch(() => setProducts([]));
  }, []);

  const groups = {};
  (products || []).forEach((p) => {
    const b = baseOf(p.id);
    (groups[b] = groups[b] || []).push(p);
  });
  // order countries by number of titles (most first), then name
  const order = Object.keys(groups).sort((a, b) => groups[b].length - groups[a].length || a.localeCompare(b));
  const nameFor = (b) => {
    const ay = groups[b].find((p) => yearOf(p.id) === null);
    return ay ? ay.title.replace(/\s*World Champions.*$/i, "") : b.charAt(0).toUpperCase() + b.slice(1);
  };
  const sortGroup = (arr) => [...arr].sort((a, b) => (yearOf(a.id) ?? -1) - (yearOf(b.id) ?? -1));

  const yearProducts = (products || []).filter((p) => yearOf(p.id) !== null);
  const decades = Array.from(new Set(yearProducts.map((p) => Math.floor(yearOf(p.id) / 10) * 10))).sort();
  const filtered = decade
    ? yearProducts.filter((p) => Math.floor(yearOf(p.id) / 10) * 10 === decade).sort((a, b) => yearOf(a.id) - yearOf(b.id))
    : [];

  return (
    <div className="bg-background min-h-screen" data-testid="champions-page">
      <Seo
        title="World Champions Collection — Every World Cup Winner | Jersey Blend"
        description="Shop the World Champions collection: original athletic designs for every FIFA World Cup winning nation, with All-Years editions and individual winning-year tees & hoodies. Add your name and number."
        keywords="world champions soccer shirt, world cup winners hoodie, 1970 brazil shirt, argentina 2022 champions tee, custom world cup jersey"
        path="/collections/champions"
      />
      <div className="px-6 md:px-12 lg:px-16 pt-16 pb-10 border-b border-white/10">
        <p className="text-xs uppercase tracking-[0.3em] font-bold text-primary mb-4 flex items-center gap-2">
          <Trophy className="w-4 h-4" /> World Champions
        </p>
        <h1 className="font-anton text-4xl sm:text-5xl lg:text-6xl uppercase leading-none text-white">
          Every World Cup Winner
        </h1>
        <p className="text-white/60 mt-6 leading-relaxed max-w-2xl text-base">
          Celebrate football's greatest nations. Each champion comes as an All-Years edition and as
          individual winning-year designs — arched name, gold trophy and title stars. Personalize the
          back with your name and number, in 7 garment colors.
        </p>
        <div className="flex flex-wrap gap-3 mt-8 text-xs uppercase tracking-[0.15em] font-bold">
          {["soccer", "nfl", "nba"].map((s) => (
            <Link key={s} to={`/collections/${s}`} className="px-5 py-2.5 border bg-transparent text-white border-white/20 hover:border-primary hover:text-primary transition-colors">
              {s === "nfl" ? "NFL" : s === "nba" ? "NBA" : "Soccer"}
            </Link>
          ))}
        </div>
      </div>

      <div className="px-6 md:px-12 lg:px-16 py-12">
        {/* Winning-year filter */}
        {products && (
          <div className="flex flex-wrap gap-2 mb-10" data-testid="year-filter">
            <button
              data-testid="decade-all"
              onClick={() => setDecade(null)}
              className={`px-4 py-2 text-[11px] font-bold uppercase tracking-[0.15em] border transition-colors ${decade === null ? "bg-primary text-black border-primary" : "bg-transparent text-white/70 border-white/20 hover:border-primary hover:text-primary"}`}
            >
              By Country
            </button>
            {decades.map((d) => (
              <button
                key={d}
                data-testid={`decade-${d}`}
                onClick={() => setDecade(d)}
                className={`px-4 py-2 text-[11px] font-bold uppercase tracking-[0.15em] border transition-colors ${decade === d ? "bg-primary text-black border-primary" : "bg-transparent text-white/70 border-white/20 hover:border-primary hover:text-primary"}`}
              >
                {d}s
              </button>
            ))}
          </div>
        )}

        {products === null ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : decade !== null ? (
          <section data-testid={`decade-results-${decade}`}>
            <div className="flex items-baseline justify-between border-b border-white/10 pb-3 mb-6">
              <h2 className="font-anton text-2xl sm:text-3xl uppercase tracking-tight text-white">{decade}s Champions</h2>
              <span className="text-xs uppercase tracking-[0.2em] text-primary font-bold">{filtered.length} {filtered.length === 1 ? "title" : "titles"}</span>
            </div>
            {filtered.length === 0 ? (
              <p className="text-white/50 py-16 text-center uppercase tracking-widest font-bold">No titles this decade.</p>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6 md:gap-8">
                {filtered.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
              </div>
            )}
          </section>
        ) : (
          order.map((b) => (
            <section key={b} className="mb-16" data-testid={`champion-group-${b}`}>
              <div className="flex items-baseline justify-between border-b border-white/10 pb-3 mb-6">
                <h2 className="font-anton text-2xl sm:text-3xl uppercase tracking-tight text-white">{nameFor(b)}</h2>
                <span className="text-xs uppercase tracking-[0.2em] text-primary font-bold">
                  {groups[b].filter((p) => yearOf(p.id)).length} {groups[b].filter((p) => yearOf(p.id)).length === 1 ? "title" : "titles"}
                </span>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6 md:gap-8">
                {sortGroup(groups[b]).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
              </div>
            </section>
          ))
        )}
      </div>
    </div>
  );
}
